import React, { useState, useEffect } from 'react';
import { payoutAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const Payouts = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolder: user?.name || '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const response = await payoutAPI.getMyPayouts();
      setPayouts(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load payouts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.amount || !formData.bankName || !formData.accountNumber || !formData.ifscCode) {
      setError('Please fill in all fields');
      return;
    }

if (parseFloat(formData.amount) < 1000) {
      setError('Minimum payout amount is Rs. 1000');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await payoutAPI.requestPayout({
        amount: parseFloat(formData.amount),
        bankDetails: {
          accountNumber: formData.accountNumber,
          ifscCode: formData.ifscCode,
          bankName: formData.bankName,
          accountHolder: formData.accountHolder,
        },
      });

      alert('Payout request submitted successfully!');
      setFormData({
        amount: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        accountHolder: user?.name || '',
      });
      setShowForm(false);
      fetchPayouts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit payout request');
      console.error('Payout error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
      case 'APPROVED':
        return 'bg-blue-100 text-blue-800 border border-blue-300';
      case 'PROCESSED':
        return 'bg-green-100 text-green-800 border border-green-300';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border border-green-300';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary mb-2">Payouts & Withdrawals</h1>
          <p className="text-gray-500">Manage your payout requests</p>
          <p className="text-sm text-gray-500 mt-1">
            Closing dates: 1–15 and 16–30 monthly. Payouts processed on the 20th (for 1–15) and the 5th (for 16–30).
            Minimum ₹1000 for each request. 10% service charge, 5% TDS & 5% admin deductions.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-semibold"
        >
          {showForm ? 'Cancel' : 'Request Payout'}
        </button>
      </div>

      {/* Payout Request Form */}
      {showForm && (
        <div className="card bg-white rounded-lg shadow-md p-8 mb-10">
          <h2 className="text-2xl font-bold text-secondary mb-6">Request a Payout</h2>

          {error && (
            <div className="alert alert-error mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (Rs.)</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter amount"
                  step="0.01"
                  min="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
                <small className="text-gray-500 mt-1">Minimum payout amount: Rs. 1000 (10% service, 5% TDS, 5% admin charges apply)</small>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="E.g., HDFC Bank"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Account Holder Name</label>
                <input
                  type="text"
                  name="accountHolder"
                  value={formData.accountHolder}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  placeholder="Your account number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">IFSC Code</label>
                <input
                  type="text"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleInputChange}
                  placeholder="E.g., HDFC0001234"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
              </div>
            </div>

            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 transition-colors font-bold"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-secondary rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Payout History */}
      <div className="card bg-white rounded-lg shadow-md overflow-hidden">
        <h2 className="text-2xl font-bold text-secondary p-8 border-b border-gray-200">Payout History</h2>

        {payouts.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="text-lg">No payouts yet. Request a payout to get started!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Request Date</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Bank Details</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Processed Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payouts?.map((payout, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {new Date(payout.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-sm font-bold text-primary">Rs. {payout.amount?.toLocaleString()}</div>
                      {payout.charges && (
                        <div className="text-xs text-gray-500">Charges: Rs. {payout.charges.toLocaleString()}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-gray-700">
                        <div className="text-xs font-semibold">{payout.bankDetails?.accountNumber}</div>
                        <div className="text-xs text-gray-500">{payout.bankDetails?.bankName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {payout.updatedAt ? new Date(payout.updatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-lg font-bold text-blue-900 mb-3">Payout Guidelines</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>✓ Minimum payout amount: Rs. 100</li>
          <li>✓ Payouts are processed within 3-5 business days</li>
          <li>✓ Make sure your bank details are correct before submitting</li>
          <li>✓ Processing charges may apply (check your region)</li>
          <li>✓ You can track your payout status here</li>
        </ul>
      </div>
    </div>
  );
};

export default Payouts;